import { useMemo, useState } from "react";
import { TrendingUp } from "lucide-react";
import "./SipCalculator.css";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Math.round(n));

function sipFutureValue(P, annualRate, years) {
  const r = annualRate / 12 / 100;
  const n = years * 12;
  if (r === 0) return P * n;
  return P * (((Math.pow(1 + r, n) - 1) / r) * (1 + r));
}

function buildSeries(P, annualRate, years) {
  const points = [];
  for (let y = 0; y <= years; y++) {
    const invested = P * y * 12;
    const wealth = y === 0 ? 0 : sipFutureValue(P, annualRate, y);
    points.push({ year: y, invested, wealth });
  }
  return points;
}

function SipChart({ series }) {
  const w = 360;
  const h = 180;
  const pad = { t: 16, r: 12, b: 28, l: 12 };
  const maxY = Math.max(...series.map((p) => p.wealth), 1);
  const maxX = Math.max(series.length - 1, 1);

  const x = (i) => pad.l + (i / maxX) * (w - pad.l - pad.r);
  const y = (v) => pad.t + (1 - v / maxY) * (h - pad.t - pad.b);

  const wealthLine = series
    .map((p, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(p.wealth)}`)
    .join(" ");
  const investedLine = series
    .map((p, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(p.invested)}`)
    .join(" ");
  const area = `${wealthLine} L${x(maxX)},${y(0)} L${x(0)},${y(0)} Z`;

  return (
    <div className="sip-chart">
      <svg viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Wealth vs invested over years">
        <defs>
          <linearGradient id="sipArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--pm-green-bright)" stopOpacity="0.35" />
            <stop offset="100%" stopColor="var(--pm-green-bright)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75, 1].map((t) => (
          <line
            key={t}
            x1={pad.l}
            x2={w - pad.r}
            y1={y(maxY * t)}
            y2={y(maxY * t)}
            className="sip-chart__grid"
          />
        ))}
        <path d={area} fill="url(#sipArea)" />
        <path d={investedLine} className="sip-chart__invested" />
        <path d={wealthLine} className="sip-chart__wealth" />
        <text x={pad.l} y={h - 6} className="sip-chart__axis">
          Yr 0
        </text>
        <text x={w - pad.r} y={h - 6} className="sip-chart__axis" textAnchor="end">
          Yr {maxX}
        </text>
      </svg>
      <div className="sip-chart__legend">
        <span>
          <i className="sip-dot sip-dot--wealth" /> Wealth
        </span>
        <span>
          <i className="sip-dot sip-dot--invested" /> Invested
        </span>
      </div>
    </div>
  );
}

export default function SipCalculator() {
  const [monthly, setMonthly] = useState(10000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(15);

  const { invested, wealth, gains, series } = useMemo(() => {
    const fv = sipFutureValue(monthly, rate, years);
    const inv = monthly * years * 12;
    return {
      invested: inv,
      wealth: fv,
      gains: fv - inv,
      series: buildSeries(monthly, rate, years),
    };
  }, [monthly, rate, years]);

  return (
    <section className="calc-card sip-calc" id="sip">
      <div className="calc-card__head">
        <div className="calc-card__icon">
          <TrendingUp size={22} />
        </div>
        <div>
          <h2>SIP Calculator</h2>
          <p>Estimate the future value of your monthly systematic investments.</p>
        </div>
      </div>

      <div className="calc-layout">
        <div>
          <div className="calc-field">
            <div className="calc-field__top">
              <label htmlFor="sip-monthly">Monthly investment</label>
              <input
                id="sip-monthly"
                className="calc-field__input"
                type="number"
                min={500}
                max={500000}
                step={500}
                value={monthly}
                onChange={(e) => setMonthly(Number(e.target.value) || 0)}
              />
            </div>
            <input
              className="calc-range"
              type="range"
              min={500}
              max={100000}
              step={500}
              value={Math.min(monthly, 100000)}
              onChange={(e) => setMonthly(Number(e.target.value))}
            />
          </div>
          <div className="calc-field">
            <div className="calc-field__top">
              <label htmlFor="sip-rate">Expected return (% p.a.)</label>
              <input
                id="sip-rate"
                className="calc-field__input"
                type="number"
                min={1}
                max={30}
                step={0.5}
                value={rate}
                onChange={(e) => setRate(Number(e.target.value) || 0)}
              />
            </div>
            <input
              className="calc-range"
              type="range"
              min={1}
              max={30}
              step={0.5}
              value={rate}
              onChange={(e) => setRate(Number(e.target.value))}
            />
          </div>
          <div className="calc-field">
            <div className="calc-field__top">
              <label htmlFor="sip-years">Time period (years)</label>
              <input
                id="sip-years"
                className="calc-field__input"
                type="number"
                min={1}
                max={40}
                value={years}
                onChange={(e) => setYears(Number(e.target.value) || 1)}
              />
            </div>
            <input
              className="calc-range"
              type="range"
              min={1}
              max={40}
              value={years}
              onChange={(e) => setYears(Number(e.target.value))}
            />
          </div>
          <SipChart series={series} />
        </div>

        <div className="calc-results">
          <div className="calc-result">
            <span>Invested amount</span>
            <strong>{formatINR(invested)}</strong>
          </div>
          <div className="calc-result">
            <span>Est. returns</span>
            <strong>{formatINR(gains)}</strong>
          </div>
          <div className="calc-result calc-result--accent">
            <span>Total value</span>
            <strong>{formatINR(wealth)}</strong>
          </div>
          <p className="calc-note">
            FV = P × [((1+r)ⁿ − 1) / r] × (1+r), where r = annual÷12÷100 and n =
            years×12.
          </p>
        </div>
      </div>
    </section>
  );
}
